import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import type { RadioFormOption } from "components/shared/FormComponent/RadioFormComponent";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import StyledButton from "components/shared/StyledButton";
import { useTracker } from "contexts/tracker";
import { BodyHighlight } from "helpers/TypographyComponents";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { eraseLot, removeBeneficiaryFromLot, updatePaymentLot } from "store/paymentSlice";
import { useAppDispatch } from "store/store";
import type { TFetchStatus } from "store/types";
import { EFetchStatus } from "store/types";

import styles from "./style.module.scss";

interface IPaymentUpdateModal {
    type: "lot" | "payment";
    paymentLotId: number | undefined | null;
    handleClose: () => void;
    /**
     * Helps determine which option to set as default when opening modal.
     */
    updateType?: "date" | "erase";
    beneficiaryId?: number;
    /*
     * Context of where the payment update modal is opened from.
     */
    context?: string;
}

export enum EPaymentUpdate {
    DATE = 0,
    CANCEL = 1,
    ERASE = 2,
}

/**
 * Modal to update a payment/lot.
 */
const PaymentUpdateModal = ({
    type,
    paymentLotId,
    handleClose,
    updateType,
    beneficiaryId,
    context,
}: IPaymentUpdateModal) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const tracker = useTracker();

    const [requestPending, setRequestPending] = useState<TFetchStatus | null>();

    const defaultValues = {
        updateType: updateType === "date" ? 0 : 2,
        paymentDate: undefined,
    };
    const { handleSubmit, control, watch } = useForm({ mode: "all", defaultValues });

    const updateOptions: RadioFormOption[] = [
        {
            label: t("PartialPayment.UpdateModal.WrongDate"),
            defaultChecked: updateType === "date",
            hide: type === "payment",
            extraComponent: (
                <div style={{ marginLeft: "19px" }}>
                    <DateFormComponent
                        control={control}
                        controlName="paymentDate"
                        label={t("PartialPayment.UpdateModal.NewDate")}
                        maxDate={new Date()}
                    />
                </div>
            ),
        },
        {
            label: t("PartialPayment.UpdateModal.PaymentRejected"),
            hide: type === "payment",
            extraComponent: (
                <BodyHighlight style={{ fontSize: "0.75rem", marginLeft: "19px" }}>
                    {t("PartialPayment.UpdateModal.RejectedDescription")}
                </BodyHighlight>
            ),
        },
        {
            label: t("PartialPayment.UpdateModal.PaymentCancelled"),
            defaultChecked: updateType === "erase",
            extraComponent: (
                <BodyHighlight style={{ fontSize: "0.75rem", marginLeft: "19px" }}>
                    {t("PartialPayment.UpdateModal.CancelledDescription")}
                </BodyHighlight>
            ),
        },
    ];

    const onSubmit = async (form) => {
        try {
            if (tracker && context) {
                tracker.trackEvent("Button clicked", {
                    button: context,
                });
            }

            if (!paymentLotId) return;
            setRequestPending(EFetchStatus.PENDING);

            let response;

            const _updateType = +form.updateType;

            switch (_updateType) {
                case EPaymentUpdate.DATE: {
                    const date = form.paymentDate ?? new Date();
                    response = await dispatch(
                        updatePaymentLot({
                            paymentLotId,
                            paymentDate: date.toLocaleDateString("en-CA"),
                        }),
                    );
                    break;
                }

                case EPaymentUpdate.CANCEL:
                    response = await dispatch(
                        updatePaymentLot({
                            paymentLotId,
                            status: "cancelled",
                        }),
                    );
                    break;
                case EPaymentUpdate.ERASE:
                    if (type === "payment") {
                        if (!beneficiaryId) return;
                        response = await dispatch(removeBeneficiaryFromLot({ paymentLotId, beneficiaryId }));
                    } else {
                        response = await dispatch(
                            eraseLot({
                                paymentLotId,
                            }),
                        );
                    }
            }

            if (response.meta.requestStatus === "fulfilled") {
                setRequestPending(EFetchStatus.FULFILLED);
                handleClose();
            } else {
                setRequestPending(EFetchStatus.REJECTED);
            }
        } catch (err) {
            console.log({ err });
            toast.error(`${t("ServerError.InternalError")}`);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles["payment-update-form"]}>
            <RadioFormComponent control={control} options={updateOptions} label="" controlName="updateType" column />
            <StyledButton buttonType="primary" type="submit" disabled={requestPending === EFetchStatus.PENDING}>
                {t("PartialPayment.UpdateModal.ApplyUpdate")}
            </StyledButton>
        </form>
    );
};

export default PaymentUpdateModal;
